import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, Eye, EyeOff } from "lucide-react";
import { Stars } from "../components/Stars";
import { api, formatApiErrorDetail } from "../lib/api";

export default function AdminReviews() {
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState("all");

    const refresh = async () => {
        const params = {};
        if (filter === "approved") params.approved = true;
        if (filter === "pending") params.approved = false;
        const { data } = await api.get("/admin/reviews", { params });
        setItems(data.items || []);
    };

    useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [filter]);

    const toggleApproved = async (r) => {
        try {
            await api.patch(`/admin/reviews/${r.id}`, { approved: !r.approved });
            toast.success(r.approved ? "Hidden from storefront" : "Approved");
            refresh();
        } catch (e2) { toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Could not update"); }
    };

    const remove = async (r) => {
        if (!window.confirm("Delete this review?")) return;
        try {
            await api.delete(`/admin/reviews/${r.id}`);
            toast.success("Review deleted");
            refresh();
        } catch (e2) { toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Failed"); }
    };

    return (
        <div data-testid="admin-reviews-page">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
                <div>
                    <span className="label-eyebrow">Trust signals</span>
                    <h1 className="mt-2 font-display text-4xl text-inkDark">Reviews</h1>
                    <p className="mt-1 text-inkMuted">{items.length} reviews shown.</p>
                </div>
                <div className="flex items-center gap-2">
                    <label className="label-eyebrow">Show</label>
                    <select value={filter} onChange={(e) => setFilter(e.target.value)} className="rounded-full bg-ivory border border-inkDark/10 px-4 py-2 text-sm" data-testid="admin-reviews-filter">
                        <option value="all">All</option>
                        <option value="approved">Approved</option>
                        <option value="pending">Hidden</option>
                    </select>
                </div>
            </div>

            <div className="mt-8 bg-ivory rounded-2xl border border-inkDark/5 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="admin-reviews-table">
                        <thead className="bg-beige text-inkMuted">
                            <tr>
                                <th className="text-left px-4 py-3 font-medium">Product</th>
                                <th className="text-left px-4 py-3 font-medium">User</th>
                                <th className="text-left px-4 py-3 font-medium">Rating</th>
                                <th className="text-left px-4 py-3 font-medium">Title</th>
                                <th className="text-left px-4 py-3 font-medium">Approved</th>
                                <th className="text-right px-4 py-3 font-medium">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((r) => (
                                <tr key={r.id} className="border-t border-inkDark/5 align-top">
                                    <td className="px-4 py-3">
                                        {r.productSlug ? (
                                            <Link to={`/product/${r.productSlug}`} className="text-inkDark hover:text-skyblueDeep" target="_blank" rel="noreferrer">
                                                {r.productName || r.productId}
                                            </Link>
                                        ) : (
                                            <span className="text-inkDark">{r.productName || r.productId}</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-inkDark">{r.userName}</td>
                                    <td className="px-4 py-3"><Stars rating={r.rating} size={12} /></td>
                                    <td className="px-4 py-3 text-inkDark line-clamp-2 max-w-md">{r.title || "—"}</td>
                                    <td className="px-4 py-3">
                                        <span className={`text-[10px] px-2 py-1 rounded-full ${r.approved ? "bg-green-50 text-green-700" : "bg-inkDark/5 text-inkMuted"}`}>
                                            {r.approved ? "Live" : "Hidden"}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <button onClick={() => toggleApproved(r)} className="p-2 rounded-lg hover:bg-beige" title={r.approved ? "Hide" : "Approve"} data-testid={`admin-review-toggle-${r.id}`}>
                                            {r.approved ? <EyeOff size={14} /> : <Eye size={14} />}
                                        </button>
                                        <button onClick={() => remove(r)} className="p-2 rounded-lg hover:bg-red-50 text-red-600" title="Delete" data-testid={`admin-review-delete-${r.id}`}>
                                            <Trash2 size={14} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {items.length === 0 ? <tr><td colSpan={6} className="px-4 py-10 text-center text-inkMuted">No reviews yet.</td></tr> : null}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
